import classNames from "classnames";
import { SpinnerIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";
import switchStyles from "./ComponentAnatomySchematicSwitch.module.scss";

/**
 * What stands beside the track on the x-ray plate. The playground passes its
 * own sandbox feature state here, so the schematic draws the same row the live
 * stage would show.
 */
export interface SwitchSchematicContent {
    label: string | null;
    labelPlacement: "end" | "start";
}

const defaultContent: SwitchSchematicContent = {
    label: "Notify me",
    labelPlacement: "end",
};

/** Mirrors `Switch`: a pill track, the thumb 2px from its edge, the real spinner arc in the thumb. */
export const SwitchSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: SwitchSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const thumbSize = read("thumb", "width");
    const spinnerSize = read("spinner", "width");
    const ringSpread = read("focusRing", "box-shadow-spread");
    const ringColor = read("focusRing", "box-shadow-color");
    const thumbTransform =
        read("thumb", "transform") === "none"
            ? undefined
            : `translateX(${read("thumb", "travel")})`;

    return (
        <span
            className={classNames(
                styles.schematic,
                styles.controlSchematic,
                content.labelPlacement === "start" && switchStyles.labelStart,
            )}
            style={{ columnGap: read("label", "column-gap") }}
        >
            <span
                className={styles.control}
                style={{
                    width: read("border", "width"),
                    height: read("border", "height"),
                }}
            >
                {ringSpread !== undefined && ringColor !== undefined && (
                    <Part
                        partId="focusRing"
                        highlight={highlight}
                        className={styles.controlRing}
                        hitInset={`calc(-1 * ${ringSpread})`}
                        style={{
                            borderRadius: radius,
                            boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                        }}
                    />
                )}
                <Part
                    partId="border"
                    highlight={highlight}
                    className={styles.controlBorder}
                    style={{
                        borderColor: read("border", "border-color"),
                        borderWidth: read("border", "border-width"),
                        borderRadius: radius,
                    }}
                />
                <Part
                    partId="fill"
                    highlight={highlight}
                    className={styles.controlFill}
                    style={{
                        background: read("fill", "background"),
                        borderRadius: radius,
                    }}
                />
                <Part
                    partId="thumb"
                    highlight={highlight}
                    className={switchStyles.thumb}
                    style={{
                        width: thumbSize,
                        height: thumbSize,
                        background: read("thumb", "background"),
                        borderRadius: read("thumb", "border-radius"),
                        transform: thumbTransform,
                    }}
                >
                    {read("spinner", "opacity") === "1" && (
                        <Part
                            partId="spinner"
                            highlight={highlight}
                            className={switchStyles.spinner}
                            style={{
                                width: spinnerSize,
                                height: spinnerSize,
                                color: read("spinner", "stroke"),
                            }}
                        >
                            <SpinnerIcon />
                        </Part>
                    )}
                </Part>
            </span>
            {content.label !== null && (
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.controlLabel}
                    style={{
                        color: read("label", "color"),
                        fontSize: read("label", "font-size"),
                        fontWeight: read("label", "font-weight"),
                        lineHeight: read("label", "line-height"),
                        letterSpacing: read("label", "letter-spacing"),
                    }}
                >
                    {content.label}
                </Part>
            )}
        </span>
    );
};
